import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import {
  ChevronDown,
  Menu,
  X,
  Sparkles,
  Palette,
  Megaphone,
  Code2,
  Bot,
  PenTool,
  LayoutGrid,
  ArrowRight,
  ArrowUp,
  Home,
  Briefcase,
  FolderOpen,
  Phone,
  BookOpen,
  BarChart3,
  Store,
} from "lucide-react";

const SERVICES = [
  { name: "Branding & Identity", icon: Palette, color: "text-violet-600", desc: "Logo, visual identity & brand strategy", href: "/services#branding" },
  { name: "Digital Marketing", icon: Megaphone, color: "text-purple-600", desc: "SEO, social media & growth campaigns", href: "/services#marketing" },
  { name: "Web & App Development", icon: Code2, color: "text-indigo-600", desc: "Custom websites, apps & platforms", href: "/services#development" },
  { name: "UI/UX Design", icon: PenTool, color: "text-fuchsia-600", desc: "Beautiful interfaces & experiences", href: "/services#design" },
  { name: "AI Automation", icon: Bot, color: "text-violet-500", desc: "Chatbots, workflows & smart tools", href: "/services#ai" },
  { name: "Content Creation", icon: LayoutGrid, color: "text-purple-500", desc: "Copy, video & creative assets", href: "/services#content" },
  { name: "Offline Marketing", icon: Store, color: "text-amber-600", desc: "Signage, in-store decor & branding", href: "/offline-marketing", highlight: true },
];

const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services", hasDropdown: true },
  { label: "Work", to: "/transformations" },
  { label: "Studio", to: "/studio" },
  { label: "Pricing", to: "/pricing" },
  { label: "Platform", to: "/platform" },
  { label: "BusiMag", to: "/busimag" },
  { label: "About", to: "/about" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showBackTop, setShowBackTop] = useState(false);
  const dropdownRef = useRef(null);
  const lastNavRef = useRef({ to: null, at: 0 });
  const location = useLocation();
  const navigate = useNavigate();

  const { scrollYProgress } = useScroll();
  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    setScrollProgress(latest);
  });

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
      setShowBackTop(window.scrollY > 600);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
    lastNavRef.current = { to: null, at: 0 };
  }, [location.pathname]);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const isActive = (to) => location.pathname === to;

  const handleNavClick = (e, to, { closeMobile = false } = {}) => {
    const now = Date.now();
    const isDuplicateClick =
      lastNavRef.current.to === to && now - lastNavRef.current.at < 700;

    if (isDuplicateClick) {
      e.preventDefault();
      return;
    }

    if (location.pathname === to) {
      e.preventDefault();
      setServicesOpen(false);
      if (closeMobile) {
        setMobileOpen(false);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    lastNavRef.current = { to, at: now };
    setServicesOpen(false);
    if (closeMobile) {
      setMobileOpen(false);
    }
  };

  return (
    <>
      {/* Scroll progress bar */}
      <div
        className="scroll-progress-bar"
        style={{ transform: `scaleX(${scrollProgress})` }}
      />

      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 100, damping: 20 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "py-2 px-4 md:px-8"
            : "py-3 px-4 md:px-8"
        }`}
      >
        <div
          className={`max-w-7xl mx-auto transition-all duration-500 ${
            scrolled
              ? "glass-strong rounded-2xl px-6 py-2 shadow-apple-lg"
              : "bg-transparent px-2 py-1"
          }`}
        >
          <div className="flex items-center justify-between">
            {/* Logo */}
            <button
              onClick={() => {
                if (location.pathname === "/") {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                  window.dispatchEvent(new CustomEvent("busigrow:replay-intro"));
                } else {
                  navigate("/");
                }
              }}
              className="flex items-center gap-2 group cursor-pointer"
            >
              <div className="relative">
                <img
                  src="/assets/images/Busigrow_Registered-1751003166599.png"
                  alt="BusiGrow"
                  className="h-16 sm:h-[4.5rem] w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                />
              </div>
            </button>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {NAV_LINKS.map((link) =>
                link.hasDropdown ? (
                  <div key={link.label} className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setServicesOpen(!servicesOpen)}
                      className={`flex items-center gap-1 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                        servicesOpen || isActive(link.to)
                          ? "text-violet-700 bg-violet-50"
                          : "text-slate-600 hover:text-violet-700 hover:bg-violet-50/60"
                      }`}
                    >
                      {link.label}
                      <ChevronDown
                        className={`w-3.5 h-3.5 transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`}
                      />
                    </button>

                    <AnimatePresence>
                      {servicesOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 8, scale: 0.96 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 8, scale: 0.96 }}
                          transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                          className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[420px] rounded-2xl p-3 bg-white/95 border border-slate-200/90 shadow-[0_20px_50px_rgba(15,23,42,0.18)]"
                        >
                          <div className="grid grid-cols-2 gap-1">
                            {SERVICES.filter(s => !s.highlight).map((s) => (
                              <button
                                key={s.name}
                                onClick={() => {
                                  navigate(s.href);
                                  setServicesOpen(false);
                                }}
                                className="flex items-start gap-3 p-3 rounded-xl hover:bg-violet-50/80 transition-all duration-200 text-left group/item"
                              >
                                <div className="p-2 rounded-lg bg-violet-50 group-hover/item:bg-violet-100 transition-colors">
                                  <s.icon className={`w-4 h-4 ${s.color}`} />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-slate-800">{s.name}</p>
                                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{s.desc}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                          {/* Offline Marketing — highlighted standalone */}
                          {SERVICES.filter(s => s.highlight).map((s) => (
                            <button
                              key={s.name}
                              onClick={() => {
                                navigate(s.href);
                                setServicesOpen(false);
                              }}
                              className="flex items-center gap-3 w-full mt-1 p-3 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 border border-amber-200/60 transition-all duration-200 text-left group/item"
                            >
                              <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500">
                                <s.icon className="w-4 h-4 text-white" />
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-semibold text-slate-800">{s.name}</p>
                                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{s.desc}</p>
                              </div>
                              <span className="text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-amber-500 to-orange-500 text-white px-2 py-0.5 rounded-full">New</span>
                            </button>
                          ))}
                          <div className="mt-2 pt-2 border-t border-slate-100">
                            <Link
                              to="/services"
                              onClick={() => setServicesOpen(false)}
                              className="flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-violet-600 hover:text-violet-700 hover:bg-violet-50 rounded-xl transition-all"
                            >
                              View All Services
                              <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={link.label}
                    to={link.to}
                    onClick={(e) => handleNavClick(e, link.to)}
                    className={`relative px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                      isActive(link.to)
                        ? "text-violet-700 bg-violet-50"
                        : "text-slate-600 hover:text-violet-700 hover:bg-violet-50/60"
                    }`}
                  >
                    {link.label}
                    {isActive(link.to) && (
                      <motion.div
                        layoutId="activeNav"
                        className="absolute -bottom-0.5 left-3 right-3 h-0.5 bg-gradient-to-r from-violet-500 to-purple-500 rounded-full"
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                      />
                    )}
                  </Link>
                )
              )}
            </nav>

            {/* CTA + Mobile Toggle */}
            <div className="flex items-center gap-3">
              <Link
                to="/contact"
                className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white text-sm font-semibold rounded-xl shadow-apple hover:shadow-apple-lg hover:scale-[1.03] active:scale-[0.97] transition-all duration-300 btn-shine"
              >
                <Sparkles className="w-4 h-4" />
                Get Started
              </Link>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="md:hidden p-2 rounded-xl text-slate-600 hover:bg-violet-50 transition-colors"
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 md:hidden"
          >
            <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="absolute right-0 top-0 bottom-0 w-[85%] max-w-sm bg-white shadow-apple-xl p-5 pt-20 overflow-y-auto pb-[calc(1.5rem+env(safe-area-inset-bottom))]"
            >
              {/* User greeting */}
              <div className="mb-5 px-2">
                <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1">Navigate</p>
              </div>

              <div className="space-y-0.5">
                {NAV_LINKS.map((link) => (
                  <React.Fragment key={link.label}>
                    <Link
                      to={link.to}
                      onClick={(e) => handleNavClick(e, link.to, { closeMobile: true })}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-all ${
                        isActive(link.to) ? "text-violet-700 bg-violet-50" : "text-slate-700 hover:bg-violet-50/60"
                      }`}
                    >
                      {link.label === "Home" && <Home className="w-4 h-4" />}
                      {link.label === "Services" && <Briefcase className="w-4 h-4" />}
                      {link.label === "Work" && <FolderOpen className="w-4 h-4" />}
                      {link.label === "Studio" && <PenTool className="w-4 h-4" />}
                      {link.label === "Pricing" && <BarChart3 className="w-4 h-4" />}
                      {link.label === "Platform" && <LayoutGrid className="w-4 h-4" />}
                      {link.label === "BusiMag" && <BookOpen className="w-4 h-4" />}
                      {link.label === "About" && <Sparkles className="w-4 h-4" />}
                      {link.label}
                    </Link>
                    {link.hasDropdown && (
                      <div className="ml-3 pl-4 border-l-2 border-violet-100 space-y-0.5 my-1">
                        {SERVICES.map((s) => (
                          <button
                            key={s.name}
                            onClick={() => { navigate(s.href); setMobileOpen(false); }}
                            className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-violet-50/60 transition-all text-left"
                          >
                            <div className="w-7 h-7 rounded-lg bg-violet-50 flex items-center justify-center shrink-0">
                              <s.icon className={`w-3.5 h-3.5 ${s.color}`} />
                            </div>
                            {s.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              <div className="mt-5 pt-5 border-t border-slate-100 space-y-0.5">
                <Link
                  to="/pricing"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium text-slate-700 hover:bg-violet-50/60 transition-all"
                >
                  <BarChart3 className="w-4 h-4" />
                  Pricing
                </Link>
                <Link
                  to="/studio"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium text-slate-700 hover:bg-violet-50/60 transition-all"
                >
                  <PenTool className="w-4 h-4" />
                  Studio
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium text-slate-700 hover:bg-violet-50/60 transition-all"
                >
                  <Phone className="w-4 h-4" />
                  Contact
                </Link>
              </div>

              <div className="mt-5">
                <Link
                  to="/contact"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold rounded-xl shadow-apple"
                >
                  <Sparkles className="w-4 h-4" />
                  Get Started
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Bottom Navigation Bar */}
      <MobileBottomNav currentPath={location.pathname} />

      {/* Back to top button */}
      <AnimatePresence>
        {showBackTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 10 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="back-to-top"
            aria-label="Back to top"
          >
            <ArrowUp className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
}

/* ─── Mobile Bottom Nav (visible < md) ─── */
const BOTTOM_PRIMARY = [
  { label: "Home", to: "/", icon: Home },
  { label: "Services", to: "/services", icon: Briefcase },
  { label: "More", to: null, icon: LayoutGrid },
  { label: "Blog", to: "/busimag", icon: BookOpen },
  { label: "Contact", to: "/contact", icon: Phone },
];

const MORE_LINKS = [
  { label: "Our Work", to: "/transformations", icon: FolderOpen, color: "bg-violet-100 text-violet-600" },
  { label: "Platform", to: "/platform", icon: Bot, color: "bg-purple-100 text-purple-600" },
  { label: "Pricing", to: "/pricing", icon: BarChart3, color: "bg-indigo-100 text-indigo-600" },
  { label: "Studio", to: "/studio", icon: PenTool, color: "bg-fuchsia-100 text-fuchsia-600" },
  { label: "About Us", to: "/about", icon: Sparkles, color: "bg-violet-100 text-violet-600" },
];

function MobileBottomNav({ currentPath }) {
  const [moreOpen, setMoreOpen] = useState(false);

  return (
    <>
      {/* "More" popup overlay */}
      <AnimatePresence>
        {moreOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[55] md:hidden"
            onClick={() => setMoreOpen(false)}
          >
            <div className="absolute inset-0 bg-black/20 backdrop-blur-[2px]" />
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="absolute bottom-[4.5rem] left-4 right-4 bg-white rounded-2xl shadow-apple-xl p-4 pb-5"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 px-1">Quick Access</p>
              <div className="grid grid-cols-3 gap-2">
                {MORE_LINKS.map((item) => {
                  const active = currentPath === item.to;
                  return (
                    <Link
                      key={item.label}
                      to={item.to}
                      onClick={() => setMoreOpen(false)}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all ${
                        active ? "bg-violet-50" : "hover:bg-slate-50 active:bg-slate-100"
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.color}`}>
                        <item.icon className="w-5 h-5" />
                      </div>
                      <span className={`text-xs font-medium ${active ? "text-violet-600" : "text-slate-600"}`}>
                        {item.label}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom tab bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white/90 backdrop-blur-md border-t border-slate-200/60 pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-center justify-around px-1 py-1.5">
          {BOTTOM_PRIMARY.map((tab) => {
            if (tab.to === null) {
              return (
                <button
                  key={tab.label}
                  onClick={() => setMoreOpen(!moreOpen)}
                  className={`flex flex-col items-center gap-0.5 min-w-0 px-2 py-1 rounded-xl transition-colors duration-200 ${
                    moreOpen ? "text-violet-600" : "text-slate-400"
                  }`}
                >
                  <tab.icon className={`w-5 h-5 ${moreOpen ? "text-violet-600" : "text-slate-400"}`} />
                  <span className={`text-[10px] font-medium truncate ${moreOpen ? "text-violet-600" : "text-slate-400"}`}>
                    {tab.label}
                  </span>
                  {moreOpen && (
                    <motion.div
                      layoutId="bottomNav"
                      className="w-1 h-1 rounded-full bg-violet-600 mt-0"
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                </button>
              );
            }
            const active = currentPath === tab.to || (tab.to !== "/" && currentPath.startsWith(tab.to));
            return (
              <Link
                key={tab.label}
                to={tab.to}
                onClick={() => setMoreOpen(false)}
                className={`flex flex-col items-center gap-0.5 min-w-0 px-2 py-1 rounded-xl transition-colors duration-200 ${
                  active ? "text-violet-600" : "text-slate-400"
                }`}
              >
                <tab.icon className={`w-5 h-5 ${active ? "text-violet-600" : "text-slate-400"}`} />
                <span className={`text-[10px] font-medium truncate ${active ? "text-violet-600" : "text-slate-400"}`}>
                  {tab.label}
                </span>
                {active && (
                  <motion.div
                    layoutId="bottomNav"
                    className="w-1 h-1 rounded-full bg-violet-600 mt-0"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
}
